import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DISC_FORMATS,
  bestFormat,
  discCount,
  displayTitle,
  filterAndSortMovies,
  genreName,
  locationName,
  movieLocationIds,
  posterUrl,
  type AppSettings,
  type Movie,
} from '@physical-movies/core';
import { useCollection, useI18n } from '../app/context';
import { Button } from '../components/ui';
import { readStoredFilter } from '../lib/filterStorage';
import { useFormatLabels } from '../lib/labels';
import styles from './print.module.css';

/** A cover stuck behind a dead connection must not block the whole page. */
const IMAGE_WAIT_MS = 15_000;

/**
 * How long to leave the button held if the browser never says it finished.
 * Generous on purpose: somebody picking a printer, or a save location for a
 * PDF, can easily take a minute, and re-enabling underneath them would invite
 * exactly the double press this is here to stop.
 */
const PRINT_GIVE_UP_MS = 120_000;

/**
 * Films per printed row.
 *
 * The cards are laid out a row at a time rather than as one long grid,
 * because a grid is the one layout a browser will not paginate properly: a
 * grid item spanning a page boundary gets sliced in half instead of being
 * pushed over, whatever `break-inside` says. A row is an ordinary block box,
 * and `break-inside: avoid` on a block box is honoured — so the page breaks
 * between rows and no film is ever cut in two.
 */
const PRINT_COLUMNS = 5;

function inRows<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

/**
 * The shelf on paper.
 *
 * Made for the half hour before people come over: print it, put it on the
 * table, and everyone can look through what there is at once instead of
 * passing a phone around.
 *
 * It reads the same stored filter as the collection screen and the same sort
 * preference, so what comes out of the printer is what was on the screen —
 * which is the only version of this that is any use.
 */
export function PrintList() {
  const { t, locale } = useI18n();
  const { movies, settings } = useCollection();
  const formatLabels = useFormatLabels();
  const navigate = useNavigate();

  // Read once: this screen is a snapshot, and a filter changing underneath a
  // half-printed page would be worse than a stale one.
  const [filter] = useState(readStoredFilter);

  const visible = useMemo(
    () => filterAndSortMovies(movies, filter, locale, settings),
    [movies, filter, locale, settings],
  );

  /**
   * Covers must be on the page before the print dialogue opens.
   *
   * A browser prints what has been laid out, and an image that has not
   * finished loading prints as a blank box — so the button waits until every
   * one of them has either arrived or given up. This is the whole reason this
   * is a screen and not a one-line `window.print()`.
   *
   * It asks the images themselves rather than counting `onLoad` calls: a
   * cached cover is already complete before React can attach a handler, and
   * counting would then wait forever for an event that has been and gone.
   */
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const images = [...(gridRef.current?.querySelectorAll('img') ?? [])];
    if (images.length === 0) {
      setReady(true);
      return;
    }

    let live = true;
    const arrived = (image: HTMLImageElement) =>
      image.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            image.addEventListener('load', () => resolve(), { once: true });
            image.addEventListener('error', () => resolve(), { once: true });
          });

    /**
     * Loaded is not the same as ready to draw. Whatever is still undecoded
     * when printing starts gets decoded *during* it, which is a good part of
     * why handing over the pages took so long — so the decoding is pulled
     * forward into this wait, where there is a spinner and a count to explain
     * it. A cover that will not decode is simply one that will not print.
     */
    const settle = (image: HTMLImageElement) =>
      arrived(image).then(() => image.decode().catch(() => undefined));

    // One cover stuck behind a dead connection should not hold the page
    // hostage; print what arrived.
    const cap = new Promise<void>((resolve) => setTimeout(resolve, IMAGE_WAIT_MS));

    void Promise.race([
      Promise.all(
        images.map((image) =>
          settle(image).then(() => {
            // Counted so the wait can say how far along it is: sixty-odd
            // covers is long enough that a bare "loading" reads as stuck.
            if (live) setLoaded((current) => current + 1);
          }),
        ),
      ),
      cap,
    ]).then(() => {
      if (live) setReady(true);
    });

    return () => {
      live = false;
    };
  }, [visible]);

  /**
   * Handing the page to the browser takes a while — it re-lays out four pages
   * and rasterises every cover, ten seconds and more for a full shelf — and
   * until the dialogue appears there is nothing on screen to say so. Pressing
   * the button again in that gap queues a second dialogue behind the first.
   *
   * What must not end the wait is `window.print()` returning. It hands the job
   * over and comes back at once — Safari took another ten seconds to put the
   * sheet on screen — so treating that return as "done" freed the button half
   * a second in, which is precisely the thing this was meant to prevent.
   *
   * Two signals say it is really over, because neither is enough on its own:
   * the `afterprint` event, and the print media query ceasing to match. Safari
   * has been unreliable about `afterprint` for years, and the media query is
   * the workaround that has always worked there.
   */
  const [printing, setPrinting] = useState(false);
  const escapeHatch = useRef(0);

  const stopPrinting = useCallback(() => {
    window.clearTimeout(escapeHatch.current);
    escapeHatch.current = 0;
    setPrinting(false);
  }, []);

  useEffect(() => {
    window.addEventListener('afterprint', stopPrinting);

    const printMedia = window.matchMedia('print');
    // Matching means the pages are being rendered; ceasing to match means the
    // browser has finished with them one way or the other.
    const onMediaChange = (event: MediaQueryListEvent) => {
      if (!event.matches) stopPrinting();
    };
    printMedia.addEventListener('change', onMediaChange);

    return () => {
      window.removeEventListener('afterprint', stopPrinting);
      printMedia.removeEventListener('change', onMediaChange);
      window.clearTimeout(escapeHatch.current);
    };
  }, [stopPrinting]);

  const print = () => {
    if (printing || !ready) return;
    setPrinting(true);

    // If 'afterprint' never arrives — a browser that does not fire it, a
    // dialogue dismissed in a way that does not count — the button must not
    // stay dead. Long enough not to fire while somebody is genuinely still
    // choosing a printer.
    escapeHatch.current = window.setTimeout(stopPrinting, PRINT_GIVE_UP_MS);

    // Two frames, so the spinner is painted before print() takes the main
    // thread for itself.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          window.print();
        } catch {
          // Refused outright — nothing is coming, so release the button now.
          stopPrinting();
        }
      });
    });
  };

  const imageCount = visible.filter((movie) => movie.posterPath !== null).length;

  const totalDiscs = visible.reduce((sum, movie) => sum + discCount(movie), 0);

  const summary = useMemo(() => {
    const parts: string[] = [];
    if (filter.query) parts.push(`“${filter.query}”`);
    for (const format of DISC_FORMATS) {
      if (filter.formats.includes(format)) parts.push(formatLabels.long(format));
    }
    if (filter.watchState !== 'all') {
      parts.push(filter.watchState === 'watched' ? t('filter.watched') : t('filter.unwatched'));
    }
    if (filter.loanState !== 'all') {
      parts.push(filter.loanState === 'loaned' ? t('filter.loaned') : t('filter.onShelf'));
    }
    for (const id of filter.genreIds) {
      const name = genreName(id, locale);
      if (name) parts.push(name);
    }
    for (const id of filter.locationIds) {
      const name = locationName(id, settings);
      if (name) parts.push(name);
    }
    return parts.join(' · ');
  }, [filter, formatLabels, t, locale, settings]);

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <Button onClick={() => void navigate('/')} disabled={printing}>
          {t('print.back')}
        </Button>
        <span className={styles.status}>
          {printing
            ? t('print.printing')
            : ready
              ? t('print.hint')
              : t('print.preparingCount', { loaded, total: imageCount })}
        </span>
        {(!ready || printing) && <span className={styles.spinner} aria-hidden="true" />}
        <Button variant="primary" disabled={!ready || printing} onClick={print}>
          {printing ? t('print.printing') : t('print.action')}
        </Button>
      </div>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('print.title')}</h1>
        <p className={styles.meta}>
          {t('print.subtitle', { count: visible.length, discs: totalDiscs })}
        </p>
        {summary && <p className={styles.meta}>{t('print.filtered', { summary })}</p>}
      </header>

      {visible.length === 0 ? (
        <p className={styles.meta}>{t('print.empty')}</p>
      ) : (
        <div className={styles.sheet} ref={gridRef}>
          {inRows(visible, PRINT_COLUMNS).map((row) => (
            <div className={styles.row} key={row[0]?.id}>
              {row.map((movie) => (
                <PrintCard
                  key={movie.id}
                  movie={movie}
                  locale={locale}
                  where={exceptionalLocation(movie, settings)}
                  formatLabel={(() => {
                    const format = bestFormat(movie);
                    return format ? formatLabels.short(format) : null;
                  })()}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Where this film is, but only when that is worth saying.
 *
 * Nearly everything is at the default location, so printing its name on all
 * sixty-odd cards is noise that costs a line of caption on every one. What is
 * worth knowing is the exception: the discs that are somewhere else.
 */
function exceptionalLocation(movie: Movie, settings: AppSettings): string | null {
  const ids = movieLocationIds(movie, settings);
  const elsewhere = ids.filter((id) => id !== settings.defaultLocationId);
  if (elsewhere.length === 0) return null;
  return elsewhere
    .map((id) => locationName(id, settings))
    .filter((name): name is string => name !== null)
    .join(', ');
}

function PrintCard({
  movie,
  locale,
  where,
  formatLabel,
}: {
  movie: Movie;
  locale: 'en' | 'de';
  where: string | null;
  formatLabel: string | null;
}) {
  const poster = posterUrl(movie.posterPath, 'w342');
  const format = bestFormat(movie);

  return (
    <article className={styles.card}>
      <div className={styles.art}>
        <span className={styles.spine} data-format={format ?? undefined} aria-hidden="true" />
        {poster ? (
          <img
            src={poster}
            alt=""
            /* Never lazy: an image that has not loaded prints as a blank box. */
            loading="eager"
            /* Decoding is awaited explicitly above, off the main thread. */
            decoding="async"
          />
        ) : (
          <span className={styles.noArt}>{displayTitle(movie, locale)}</span>
        )}
      </div>
      <p className={styles.cardTitle}>{displayTitle(movie, locale)}</p>
      <p className={styles.cardMeta}>
        {[movie.year, formatLabel, where].filter(Boolean).join(' · ')}
      </p>
    </article>
  );
}
